import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  DeactivateGeoItemDocument,
  type DeactivateGeoItemMutation,
  type DeactivateGeoItemMutationVariables,
  GetAreaGeoItemDetailDocument,
  GetAreaRecordGeoItemsDocument,
} from "~/features/recordGeoItems/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseGeoItemActivationProps {
  recordId: string;
  geoItemId: string;
}

/** Deactivate (soft-delete) a record geo-item. The caller owns the confirm dialog. */
export function useGeoItemActivation({ recordId, geoItemId }: UseGeoItemActivationProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [deactivateGeoItem] = useMutation<DeactivateGeoItemMutation, DeactivateGeoItemMutationVariables>(
    DeactivateGeoItemDocument,
  );

  const onDeactivate = useCallback(
    async (id: string) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        await deactivateGeoItem({
          variables: { input: { tenantId, id } },
          refetchQueries: [
            { query: GetAreaRecordGeoItemsDocument, variables: { tenantId, recordId } },
            { query: GetAreaGeoItemDetailDocument, variables: { tenantId, recordId, geoItemId } },
          ],
        });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "geoItem" }));
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [deactivateGeoItem, tenantId, recordId, geoItemId, t],
  );

  return { onDeactivate, processing };
}
